import { ref } from 'vue'
import operationRecordAPI from '@/api/operationRecord'
import { parseRecordMotion, parseRecordServoParamList, buildCombined } from '@/utils/motionCombine.js'

// 组合动作：编排选中记录的执行顺序 / 间隔，可直接执行或合成为新的操作记录
// 依赖：
//   selectedIds  - 表格勾选的记录 id 列表 (Ref)
//   showSnackbar - 提示回调 (text, color) => void
//   emitRunCombo - 触发执行的回调 (payload) => void（由组件 emit 给父级）
//   onCombined   - 合成保存成功后的回调（用于刷新列表）
export function useRecordCombo({ selectedIds, showSnackbar, emitRunCombo, onCombined }) {
  const comboDialog = ref(false)
  const comboLoading = ref(false)
  const comboItems = ref([])              // 组合动作列表（按执行顺序）
  const comboUniform = ref(true)          // 是否整组使用统一间隔
  const comboGlobalInterval = ref(1000)   // 统一间隔时间（ms）

  // 合成为操作记录
  const combineName = ref('')             // 合成后的操作名称
  const isCombining = ref(false)
  const combineDialog = ref(false)        // 合成命名弹窗

  // 打开组合动作对话框：拉取选中记录的完整详情
  const openComboDialog = async () => {
    if (selectedIds.value.length < 2) {
      showSnackbar('请至少勾选 2 条记录', 'warning')
      return
    }
    comboLoading.value = true
    try {
      const items = []
      for (const id of selectedIds.value) {
        try {
          const res = await operationRecordAPI.getDetail(id)
          const rec = res && res.data
          if (!rec) continue
          const { mcpInner, durationMs, motionArr } = parseRecordMotion(rec)
          items.push({
            id: rec.id,
            operationName: rec.operationName || '(未命名)',
            emoji: rec.emoji || '',
            mcpData: mcpInner,
            motionArr: Array.isArray(motionArr) ? motionArr : [],
            servoParamList: parseRecordServoParamList(rec),
            version: rec.version ?? null,
            servoVersion: rec.servoVersion ?? null,
            deviceId: rec.deviceId ?? (mcpInner && mcpInner.device_id) ?? null,
            hasMotion: !!mcpInner,
            durationMs,
            interval: comboGlobalInterval.value
          })
        } catch (err) {
          console.error(`获取记录 ${id} 详情失败:`, err)
        }
      }
      if (items.length === 0) {
        showSnackbar('未获取到可执行的记录数据', 'error')
        return
      }
      comboItems.value = items
      comboDialog.value = true
    } finally {
      comboLoading.value = false
    }
  }

  // 调整组合动作顺序
  const moveComboItem = (idx, dir) => {
    const arr = comboItems.value
    const ni = idx + dir
    if (ni < 0 || ni >= arr.length) return
    const tmp = arr[idx]
    arr[idx] = arr[ni]
    arr[ni] = tmp
    comboItems.value = [...arr]
  }

  // 从组合中移除某个动作
  const removeComboItem = (idx) => {
    comboItems.value.splice(idx, 1)
  }

  // 开始执行组合动作：将排好序的动作及间隔交给父组件下发
  const startCombo = () => {
    const hasAny = comboItems.value.some(it => it.hasMotion)
    if (!hasAny) {
      showSnackbar('所选记录均无可执行的动作数据', 'error')
      return
    }
    const items = comboItems.value.map((it, idx) => ({
      operationName: it.operationName,
      mcpData: it.mcpData,
      hasMotion: it.hasMotion,
      durationMs: it.durationMs,
      // 最后一个动作之后无需再等待
      intervalMs: idx === comboItems.value.length - 1
        ? 0
        : (comboUniform.value ? comboGlobalInterval.value : (it.interval || 0))
    }))
    emitRunCombo({ items })
    comboDialog.value = false
  }

  // 打开合成弹窗前的默认名称
  const buildDefaultCombineName = () => {
    const names = comboItems.value.map(it => it.operationName).filter(Boolean)
    const joined = names.join('+')
    return joined.length > 40 ? `组合动作(${names.length}个)` : joined
  }

  // 打开合成命名弹窗
  const openCombineDialog = () => {
    const actionsCount = comboItems.value.filter(it => it.hasMotion).length
    if (actionsCount < 2) {
      showSnackbar('至少需要 2 个含动作数据的记录才能合成', 'warning')
      return
    }
    combineName.value = buildDefaultCombineName()
    combineDialog.value = true
  }

  // 合成为一条操作记录并保存
  const combineToRecord = async () => {
    const actionsCount = comboItems.value.filter(it => it.hasMotion).length
    if (actionsCount < 2) {
      showSnackbar('至少需要 2 个含动作数据的记录才能合成', 'warning')
      return
    }
    const name = (combineName.value || '').trim()
    if (!name) {
      showSnackbar('请输入合成后的操作名称', 'warning')
      return
    }

    isCombining.value = true
    try {
      const { motion, servoParamList, totalMs } = buildCombined(comboItems.value, {
        uniform: comboUniform.value,
        uniformInterval: comboGlobalInterval.value
      })
      if (motion.length === 0) {
        showSnackbar('未能生成有效的合成动作', 'error')
        return
      }

      // 取第一个含动作数据的记录作为元信息来源
      const base = comboItems.value.find(it => it.hasMotion) || comboItems.value[0]
      const baseArgs = base.mcpData && base.mcpData.params && base.mcpData.params.arguments
        ? base.mcpData.params.arguments
        : {}
      const version = base.version ?? baseArgs.version ?? 1
      const deviceId = base.deviceId ?? (base.mcpData && base.mcpData.device_id) ?? null

      const mcpData = {
        device_id: deviceId,
        params: {
          name: (base.mcpData && base.mcpData.params && base.mcpData.params.name) || 'self.servo.motion',
          arguments: {
            name,
            operate: 2,
            version,
            motion
          }
        }
      }

      const payload = {
        operationName: name,
        emoji: base.emoji || null,
        version,
        deviceId,
        servoVersion: base.servoVersion ?? null,
        params: JSON.stringify({
          operationName: name,
          totalTime: totalMs,
          servoParamList
        }),
        mcpData: JSON.stringify({ mcpData })
      }

      await operationRecordAPI.save(payload)
      showSnackbar(`已合成操作记录「${name}」（时长约 ${totalMs} ms）`, 'success')
      combineDialog.value = false
      comboDialog.value = false
      if (onCombined) await onCombined()
    } catch (err) {
      console.error('合成操作记录失败:', err)
      showSnackbar(`合成失败: ${err.message}`, 'error')
    } finally {
      isCombining.value = false
    }
  }

  return {
    comboDialog, comboLoading, comboItems, comboUniform, comboGlobalInterval,
    combineName, isCombining, combineDialog,
    openComboDialog, moveComboItem, removeComboItem, startCombo,
    openCombineDialog, combineToRecord
  }
}
